import { useState } from 'react';

import type { EditCommentCallback } from '@/features/action-panel/comments/comment.type';
import { CommentsTab } from '@/features/action-panel/comments/comments-tab';
import { teamIdState } from '@/features/teams/team.state';
import { modalState } from '@/state/modal.state';

import { useCreateTeamComment } from './use-create-team-comment.mutation';
import { useDeleteTeamComment } from './use-delete-team-comment.mutation';
import { useTeamComments } from './use-team-comments.query';
import { useUpdateTeamComment } from './use-update-team-comment.mutation';
import { useAtomValue, useSetAtom } from 'jotai';

export const TeamCommentsTab = () => {
  const teamId = useAtomValue(teamIdState);
  const [loading, setLoading] = useState<boolean>(false);

  const setModal = useSetAtom(modalState);

  const { data: comments } = useTeamComments();
  const { mutateAsync: createTeamComment } = useCreateTeamComment();
  const { mutateAsync: updateTeamComment } = useUpdateTeamComment();
  const { mutateAsync: deleteTeamComment } = useDeleteTeamComment();

  if (!teamId || !comments) return null;

  const submitComment = async (comment: string) => {
    const createdComment = await createTeamComment({
      teamId,
      payload: {
        content: comment,
      },
    });

    return createdComment.id;
  };

  const openDeleteModal = async (commentId: string) => {
    setModal({
      state: 'confirmDelete',
      onConfirm: () => deleteComment(commentId),
    });
  };

  const deleteComment = async (commentId: string) => {
    setLoading(true);

    await deleteTeamComment({
      teamId,
      commentId,
    });

    setLoading(false);
  };

  const editComment: EditCommentCallback<string> = async (commentId, data) => {
    setLoading(true);

    const comment = await updateTeamComment({
      teamId,
      commentId,
      payload: {
        content: data.comment,
      },
    });

    setLoading(false);

    return comment.id;
  };

  return (
    <CommentsTab
      onSubmit={submitComment}
      comments={comments}
      onClickDelete={openDeleteModal}
      onEditSubmit={editComment}
      loading={loading}
    />
  );
};
